import { HoldingsFilters, HoldingTypeFilter } from 'api/HoldingsService'
import { getCurrentGroupId, getCurrentGroupType } from 'selectors/auth'

import { GroupTypes } from 'utils/constants/groups'
import { PortfolioTypes } from 'utils/constants/portfolio'
import {
  usePortfoliosHoldingsIds,
  useSubjectsInfiniteQuery,
} from 'utils/hooks/queries/subjects/useSubjectsInfiniteQuery'
import { SubjectFilter } from 'utils/hooks/queries/subjects/useSubjectsQuery'
import { useAppSelector } from 'utils/hooks/reduxToolkit'
import { SubjectType } from 'utils/types/subjects'

interface Props {
  textSearch?: string
  currentFilters: HoldingsFilters
  tagsNameValue?: string[]
  companiesPerPage: number
}

const getSubjectTypesFromFilters = (types: HoldingTypeFilter[]) => {
  const subjectTypes = types.reduce<SubjectType[]>((res, holdingType) => {
    switch (holdingType) {
      case HoldingTypeFilter.COMPANY:
        res.push(SubjectType.COMPANY)
        break
      case HoldingTypeFilter.FUND:
        res.push(SubjectType.FUND)
        break
      case HoldingTypeFilter.DEAL:
        res.push(SubjectType.DEAL)
        break
      default:
    }

    return res
  }, [])

  return subjectTypes.length
    ? subjectTypes
    : [SubjectType.COMPANY, SubjectType.FUND, SubjectType.DEAL]
}

export const getHoldingsFilters = (
  filters: HoldingsFilters & { textSearch?: string; groupId: string }
): SubjectFilter => {
  return {
    id: filters.idIn,
    railsId: filters.railsId,
    name: filters.textSearch,
    type: getSubjectTypesFromFilters(filters.typeIn ?? []),
    'attributesMap.isFundCompany.value': filters.typeIn?.includes(
      HoldingTypeFilter.ORGANIZATION
    )
      ? true
      : undefined,
    'attributesMap.industries.value': filters.industryId?.length
      ? {
          $elemMatch: { id: { $in: filters.industryId } },
        }
      : undefined,

    'attributesMap.locations.value': filters.locationPlaceAddress
      ? {
          $elemMatch: {
            formattedAddress: {
              $regex: filters.locationPlaceAddress,
              $options: 'i',
            },
          },
        }
      : undefined,
    'attributesMap.legalStructure.value.id': filters.legalStructureId
      ? { $in: filters.legalStructureId }
      : undefined,
    'attributesMap.private.value': filters.onlyPrivateCompany
      ? true
      : undefined,

    'tags.tagId': filters.tags?.length
      ? { $in: filters.tags.map((tag) => tag.id) }
      : undefined,
  }
}

export const useHoldingsQuery = ({
  textSearch = '',
  currentFilters,
  companiesPerPage,
}: Props) => {
  const groupId = useAppSelector(getCurrentGroupId)
  const groupType = useAppSelector(getCurrentGroupType)

  // Fetch only portfolio holdings for LPs
  const portfoliosHoldingIds = usePortfoliosHoldingsIds({
    portfolioType: [PortfolioTypes.FUND, PortfolioTypes.DEAL],
    enabled: groupType === GroupTypes.INVESTOR_GROUP,
  })

  const subjectFilters: SubjectFilter = getHoldingsFilters({
    ...currentFilters,
    railsId: portfoliosHoldingIds,
    textSearch,
    groupId,
  })

  const {
    data: holdings,
    isLoading,
    isFetchingNextPage,
    error,
    endOfPageRef: infiniteScrollRef,
  } = useSubjectsInfiniteQuery({
    filters: subjectFilters,
    sorting: {
      orderBy: currentFilters.orderBy,
      direction: currentFilters.direction,
    },
    pageSize: companiesPerPage,
  })

  return {
    isLoading,
    isFetchingNextPage,
    holdings,
    error,
    endOfPageRef: infiniteScrollRef,
  }
}
